/**
 * @fileoverview Canonical employee status taxonomy constants (CJS).
 *
 * Single source of truth (EAS-001) for the TWO independent employee
 * lifecycle axes. They were historically both called "status", which
 * caused PlannerPuffin to hide bulk-imported employees (it filtered the
 * account axis while meaning the employment axis).
 *
 *   1. ACCOUNT status (`users.accountStatus`) — app/login lifecycle.
 *      Governs who can authenticate. Best-practice onboarding ladder:
 *      Not invited (`imported`) → Invited (`invited`) → Active (`active`),
 *      plus the deactivation states inactive/suspended/terminated.
 *   2. EMPLOYMENT status (`users.employmentStatus`) — HR lifecycle.
 *      Governs roster membership (who is an employee at all).
 *
 * Disambiguation is carried by the column heading ("Access" vs
 * "Employment") + this SSOT — NOT by inventing non-overlapping value
 * words (the natural HR terms `Active`/`Suspended`/`Terminated` recur on
 * both axes; the heading disambiguates, per When I Work / BambooHR).
 *
 * Dual-use CJS + ESM. Consumed by every API (auth gates, invite, models,
 * roster reader, scheduling services) AND every web app (status badges,
 * onboarding chips, edit drawers). Do NOT spell a status string as a
 * literal anywhere else — `api/no-hardcoded-account-status-literal` (the
 * account axis) + `web/no-local-status-label-map` enforce this.
 *
 * @module @puffin-core/status-taxonomy/constants
 */

'use strict';

const { BADGE_TONE } = require('./badgeTones');

// ========== ACCOUNT (login / app-access) STATUS ==========

/**
 * App-account lifecycle (`users.accountStatus`). Only `active` may
 * authenticate (see `@puffin-core/auth-routes` login gate).
 */
const ACCOUNT_STATUS = Object.freeze({
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  SUSPENDED: 'suspended',
  INVITED: 'invited',
  TERMINATED: 'terminated',
  // Profile-only record created via bulk import (BULK-IMPORT-002): exists
  // for scheduling/payroll/skills but has NO login and has NOT been invited.
  IMPORTED: 'imported',
});

// ========== EMPLOYMENT (HR) STATUS ==========

/**
 * HR employment lifecycle (`users.employmentStatus`). CHECK-constrained
 * in the DB to exactly these four values.
 */
const EMPLOYMENT_STATUS = Object.freeze({
  ACTIVE: 'ACTIVE',
  ON_LEAVE: 'ON_LEAVE',
  SUSPENDED: 'SUSPENDED',
  TERMINATED: 'TERMINATED',
});

// ========== EMPLOYMENT TERMS (HR) ==========
//
// Q-R8CLOSE-5 (2026-09-25): the three HR vocabularies beside employment status. Each is
// CHECK-constrained in the DB to exactly these values (pos-api
// `20260203000003_add_team_member_fields`), and each had been hand-copied into pos-api (twice),
// pos-web, payday-api and payday-web — five copies of one CHECK. Every copy now reads these
// (L-R7CLOSE rows 18 + 28); an app keeps only its localised LABELS, keyed by these values.

/** Employment type (`users.employmentType`; NOT NULL, default FULL_TIME). */
const EMPLOYMENT_TYPE = Object.freeze({
  FULL_TIME: 'FULL_TIME',
  PART_TIME: 'PART_TIME',
  TEMPORARY: 'TEMPORARY',
  CONTRACT: 'CONTRACT',
});

/** Pay type (`users.payType`; nullable). */
const PAY_TYPE = Object.freeze({
  HOURLY: 'HOURLY',
  SALARY: 'SALARY',
});

/** Termination reason (`users.terminationReason`; nullable). */
const TERMINATION_REASON = Object.freeze({
  VOLUNTARY: 'VOLUNTARY',
  INVOLUNTARY: 'INVOLUNTARY',
  LAYOFF: 'LAYOFF',
  RETIREMENT: 'RETIREMENT',
});

/** Frozen value arrays (for model validation + Joi `valid(...)`). */
const ACCOUNT_STATUS_VALUES = Object.freeze(Object.values(ACCOUNT_STATUS));
const EMPLOYMENT_STATUS_VALUES = Object.freeze(Object.values(EMPLOYMENT_STATUS));
const EMPLOYMENT_TYPE_VALUES = Object.freeze(Object.values(EMPLOYMENT_TYPE));
const PAY_TYPE_VALUES = Object.freeze(Object.values(PAY_TYPE));
const TERMINATION_REASON_VALUES = Object.freeze(Object.values(TERMINATION_REASON));

/** Column defaults (mirror the DB `defaultTo`). */
const DEFAULT_ACCOUNT_STATUS = ACCOUNT_STATUS.ACTIVE;
const DEFAULT_EMPLOYMENT_STATUS = EMPLOYMENT_STATUS.ACTIVE;
const DEFAULT_EMPLOYMENT_TYPE = EMPLOYMENT_TYPE.FULL_TIME;

// ========== UNDOING A CREATED ACCOUNT (owner ruling Q-USERDEL-1, 2026-10-06) ==========
//
// "Retire DELETE (Recommended)": deactivating is the only way to remove a person, and a bulk-import
// undo deletes only the people who never accepted, deactivating anyone who already did. These two
// name that rule once, for the model that applies it (`@puffin-core/unified-models`
// `UserModel.undoCreatedUser`) and the undo paths that report it (`@puffin-core/invite-core`
// `bulkRevoke`, `@puffin-core/bulk-import`'s insert adapter).

/**
 * The statuses of a person who has never accepted an invitation, so has never signed in: created by
 * an import (`imported`) or an invitation (`invited`; an imported person who is invited moves here).
 * They have never signed in, so nothing they DID can reference them — only rows written about them
 * (their invitation, location assignments, leave balances). These are the only accounts an undo removes.
 */
const UNACCEPTED_ACCOUNT_STATUSES = Object.freeze([
  ACCOUNT_STATUS.IMPORTED,
  ACCOUNT_STATUS.INVITED,
]);

/**
 * What undoing a created account did: `removed` (it had never been accepted), `deactivated` (it had
 * been accepted and was active — moved to `inactive` through the status write, which retires its
 * credentials and ends its sessions), or `unchanged` (accepted and already not active, or gone).
 */
const ACCOUNT_UNDO_OUTCOME = Object.freeze({
  REMOVED: 'removed',
  DEACTIVATED: 'deactivated',
  UNCHANGED: 'unchanged',
});
const ACCOUNT_UNDO_OUTCOME_VALUES = Object.freeze(Object.values(ACCOUNT_UNDO_OUTCOME));

/**
 * Why a deactivation was refused (the coordinator's Q-USERDEL-2 parity build, 2026-10-07). Deactivating is the only
 * way to remove a person, so it carries the guards the retired DELETE had, fail-closed: nobody deactivates their own
 * account (`SELF`), nobody deactivates someone whose role is above their own (`ABOVE_ACTOR`; an equal role may be,
 * the role-assignment house rule), the tenant's last active Owner is never deactivated (`LAST_OWNER`; a security
 * lockout of a compromised account alone may, by owner ruling 2026-10-07), and
 * an actor whose own role cannot be resolved deactivates nobody (`ACTOR_UNRESOLVED`). Declared once for the status
 * writer that refuses (`@puffin-core/unified-models` `guardAccountDeactivation`) and the screens that word each one.
 */
const ACCOUNT_DEACTIVATION_REFUSAL = Object.freeze({
  SELF: 'DEACTIVATE_OWN_ACCOUNT',
  ABOVE_ACTOR: 'DEACTIVATE_ABOVE_OWN_ROLE',
  LAST_OWNER: 'DEACTIVATE_LAST_OWNER',
  ACTOR_UNRESOLVED: 'DEACTIVATE_ACTOR_UNRESOLVED',
});
const ACCOUNT_DEACTIVATION_REFUSAL_VALUES = Object.freeze(
  Object.values(ACCOUNT_DEACTIVATION_REFUSAL),
);

/**
 * The system acts that may deactivate an account with no person behind the click: a security lockout of a
 * compromised account, an identity provider's deprovision (SCIM), and a privacy erasure (GDPR Art. 17). Each names
 * itself to the status writer as `{ system }`; none has an own account or a role. Erasure and deprovision answer to
 * the last-Owner rule; the security lockout is exempt from it (owner ruling 2026-10-07). Declared here, beside the refusals, because the packages that perform them (`@puffin-core/sso`,
 * `@puffin-core/privacy-requests`) read this vocabulary and not the model package.
 */
const ACCOUNT_DEACTIVATION_SYSTEM_ACTOR = Object.freeze({
  SECURITY_LOCKOUT: 'security_lockout',
  IDENTITY_PROVIDER_DEPROVISION: 'identity_provider_deprovision',
  PRIVACY_ERASURE: 'privacy_erasure',
});
const ACCOUNT_DEACTIVATION_SYSTEM_ACTOR_VALUES = Object.freeze(
  Object.values(ACCOUNT_DEACTIVATION_SYSTEM_ACTOR),
);

/**
 * Why a reactivation was refused (owner ruling 2026-10-07, "rank-check reactivation"). Making someone Active again is
 * judged like deactivating them: nobody reactivates someone whose role is STRICTLY above their own (`ABOVE_ACTOR`; an
 * equal role may), and an actor whose own role cannot be resolved reactivates nobody (`ACTOR_UNRESOLVED`).
 */
const ACCOUNT_REACTIVATION_REFUSAL = Object.freeze({
  ABOVE_ACTOR: 'REACTIVATE_ABOVE_OWN_ROLE',
  ACTOR_UNRESOLVED: 'REACTIVATE_ACTOR_UNRESOLVED',
});
const ACCOUNT_REACTIVATION_REFUSAL_VALUES = Object.freeze(
  Object.values(ACCOUNT_REACTIVATION_REFUSAL),
);

/**
 * Why an administrator's password reset was refused (OWNER-RULINGS-WALK-BUILDS N61). The reset retires a team
 * member's password and every credential they hold, and they set a new one through the emailed reset, so it is judged
 * like a deactivation: nobody resets their own password this way (`SELF`; their own is the emailed reset or their
 * profile), nobody resets someone whose role is STRICTLY above their own (`ABOVE_ACTOR`; an equal role may be), an
 * actor whose own role cannot be resolved resets nobody (`ACTOR_UNRESOLVED`), and only an ACTIVE account is reset
 * (`NOT_ACTIVE`): the emailed reset serves active accounts alone, and a non-active account already holds no live
 * credential. Declared once for the judge that refuses (`@puffin-core/unified-models` `guardAccountPasswordReset`)
 * and the screens that word each one.
 */
const ACCOUNT_PASSWORD_RESET_REFUSAL = Object.freeze({
  SELF: 'PASSWORD_RESET_OWN_ACCOUNT',
  ABOVE_ACTOR: 'PASSWORD_RESET_ABOVE_OWN_ROLE',
  ACTOR_UNRESOLVED: 'PASSWORD_RESET_ACTOR_UNRESOLVED',
  NOT_ACTIVE: 'PASSWORD_RESET_NOT_ACTIVE',
});
const ACCOUNT_PASSWORD_RESET_REFUSAL_VALUES = Object.freeze(
  Object.values(ACCOUNT_PASSWORD_RESET_REFUSAL),
);

/**
 * Why an administrator's "Reset Authentication" was refused (OWNER-RULINGS-WALK-BUILDS N71). The reset retires every way
 * a team member signs in besides their password (devices and the PINs on them, passkeys, authenticator devices, MFA
 * backup codes) and ends every session, so it is judged like the password reset: nobody resets their own this way
 * (`SELF`; their own devices and codes are managed in Security Settings), nobody resets someone whose role is STRICTLY
 * above their own (`ABOVE_ACTOR`; an equal role may be), and an actor whose own role cannot be resolved resets nobody
 * (`ACTOR_UNRESOLVED`). Unlike the password reset it does NOT refuse a non-active account: it only retires, never
 * grants, and a deactivated account still holds its MFA backup codes, which a later reactivation would bring back.
 * Declared once for the judge that refuses (`@puffin-core/unified-models` `guardAccountAuthenticationReset`) and the
 * screens that word each one.
 */
const ACCOUNT_AUTHENTICATION_RESET_REFUSAL = Object.freeze({
  SELF: 'AUTHENTICATION_RESET_OWN_ACCOUNT',
  ABOVE_ACTOR: 'AUTHENTICATION_RESET_ABOVE_OWN_ROLE',
  ACTOR_UNRESOLVED: 'AUTHENTICATION_RESET_ACTOR_UNRESOLVED',
});
const ACCOUNT_AUTHENTICATION_RESET_REFUSAL_VALUES = Object.freeze(
  Object.values(ACCOUNT_AUTHENTICATION_RESET_REFUSAL),
);

/**
 * Why a manager's "Reset calendar link" was refused (VALIDATION-ECHO-001 P4; owner ruling "Never expires; employee can
 * reset it (Recommended)"). A holder of `palm:calendar-sync:reset-team` ends a team member's PalmPuffin calendar link
 * from pos-web's team list. Acting on another person's account is judged like the administrator resets: nobody resets
 * their own this way (`SELF`; their own link is reset in PalmPuffin), nobody resets someone whose role is STRICTLY
 * above their own (`ABOVE_ACTOR`; an equal role may be), and an actor whose own role cannot be resolved resets nobody
 * (`ACTOR_UNRESOLVED`). A non-active account is not refused: the reset only removes. Declared once for the judge that
 * refuses (`@puffin-core/unified-models` `guardCalendarSyncRevoke`) and the screen that words each one.
 */
const CALENDAR_SYNC_REVOKE_REFUSAL = Object.freeze({
  SELF: 'CALENDAR_SYNC_REVOKE_OWN_ACCOUNT',
  ABOVE_ACTOR: 'CALENDAR_SYNC_REVOKE_ABOVE_OWN_ROLE',
  ACTOR_UNRESOLVED: 'CALENDAR_SYNC_REVOKE_ACTOR_UNRESOLVED',
});
const CALENDAR_SYNC_REVOKE_REFUSAL_VALUES = Object.freeze(
  Object.values(CALENDAR_SYNC_REVOKE_REFUSAL),
);

/**
 * The activations no tenant person makes for someone else. Each names itself to the status writer as `{ system }`,
 * so the rank judgment, which is about one person acting on another, does not apply to it:
 * - `INVITATION_ACCEPTANCE`: an invitee accepting their own invitation (`@puffin-core/invite-core`'s acceptance).
 * - `PLATFORM_OWNER_RESTORE`: a platform staff member restoring the tenant's locked-out sole Owner (owner ruling
 *   2026-10-07, "One platform staff"). A staff member holds no tenant role, so this act carries its own rules
 *   instead: the judge refuses it with a `PLATFORM_OWNER_RESTORE_REFUSAL` code unless the tenant has no active Owner,
 *   the person holds the Owner role, and their account is a lockout (`RESTORABLE_OWNER_ACCOUNT_STATUSES`).
 */
const ACCOUNT_ACTIVATION_SYSTEM_ACTOR = Object.freeze({
  INVITATION_ACCEPTANCE: 'invitation_acceptance',
  PLATFORM_OWNER_RESTORE: 'platform_owner_restore',
});
const ACCOUNT_ACTIVATION_SYSTEM_ACTOR_VALUES = Object.freeze(
  Object.values(ACCOUNT_ACTIVATION_SYSTEM_ACTOR),
);

/**
 * Why a platform owner restore was refused (owner ruling 2026-10-07, "One platform staff"). The restore is NARROW, the
 * recovery for a tenant locked out of its own administration, never a second way to reactivate people:
 * - `TENANT_HAS_OWNER`: the tenant still has an active Owner, who can restore anyone themself (the restored person
 *   counts too, so a second restore of the same person is refused rather than re-run).
 * - `TARGET_NOT_OWNER`: the person does not hold the Owner role, so restoring them would not end the lockout.
 * - `NOT_RESTORABLE`: the account is not a lockout. It may have been erased (`terminated`), never accepted
 *   (`invited`, `imported`), or already be `active`.
 */
const PLATFORM_OWNER_RESTORE_REFUSAL = Object.freeze({
  TENANT_HAS_OWNER: 'RESTORE_TENANT_HAS_OWNER',
  TARGET_NOT_OWNER: 'RESTORE_TARGET_NOT_OWNER',
  NOT_RESTORABLE: 'RESTORE_NOT_RESTORABLE',
});
const PLATFORM_OWNER_RESTORE_REFUSAL_VALUES = Object.freeze(
  Object.values(PLATFORM_OWNER_RESTORE_REFUSAL),
);

/**
 * The account statuses a platform owner restore may move to `active`: the two a lockout leaves an account in. A
 * security lockout suspends it (`suspended`), and a deactivation leaves it `inactive`.
 */
const RESTORABLE_OWNER_ACCOUNT_STATUSES = Object.freeze([
  ACCOUNT_STATUS.INACTIVE,
  ACCOUNT_STATUS.SUSPENDED,
]);

/**
 * Why a ROLE change was refused (owner ruling 2026-10-07, "refuse demoting the last Owner"): a change that would
 * leave the tenant with no active Owner is refused, whoever asks (`LAST_OWNER`). Who may GIVE which role is the
 * assignment rule's own refusal (pos-api `assignableRole`).
 */
const ROLE_CHANGE_REFUSAL = Object.freeze({
  LAST_OWNER: 'ROLE_CHANGE_LAST_OWNER',
});
const ROLE_CHANGE_REFUSAL_VALUES = Object.freeze(Object.values(ROLE_CHANGE_REFUSAL));

/**
 * Canonical "schedulable" condition as plain data (Knex-agnostic, so it is
 * the ONE source of truth shared by both the JS `isSchedulable(user)` helper
 * and every server-side SQL placement/approval gate). A person may be placed
 * on a shift / counted as an active actor only when BOTH axes are ACTIVE.
 *
 * `isSchedulable` derives from this; API SQL gates build their `.where()` from
 * it via `applySchedulableWhere` (planner-api) so they can never drift from
 * the helper. If "schedulable" ever gains a condition, change it HERE only.
 */
const SCHEDULABLE_PREDICATE = Object.freeze({
  accountStatus: ACCOUNT_STATUS.ACTIVE,
  employmentStatus: EMPLOYMENT_STATUS.ACTIVE,
});

// ========== DISPLAY LABELS ==========

/** Human labels for the "Access" column. */
const ACCOUNT_STATUS_LABELS = Object.freeze({
  active: 'Active',
  inactive: 'Inactive',
  suspended: 'Suspended',
  invited: 'Invited',
  terminated: 'Terminated',
  // Onboarding-ladder wording (When I Work "Not Invited").
  imported: 'Not invited',
});

/** Human labels for the "Employment" column. */
const EMPLOYMENT_STATUS_LABELS = Object.freeze({
  ACTIVE: 'Active',
  ON_LEAVE: 'On leave',
  SUSPENDED: 'Suspended',
  TERMINATED: 'Terminated',
});

// ========== BADGE TONES (Q-MI-25 — `.badge[data-tone]`, via `<StatusBadgeCell>`) ==========

/**
 * Account status → badge TONE. Handed to `<StatusBadgeCell statusTones={…}>`, which resolves it
 * through `resolveBadgeTone` (the closed five-tone set) and emits `.badge[data-tone]`.
 *
 * These replace `ACCOUNT_STATUS_BADGE_CLASSES` (`'badge badge-success'`) and its derived
 * `ACCOUNT_STATUS_BADGE_VARIANTS` (`'badge-success'`): a tone is the MEANING, and the class that
 * renders it is the primitive's business, so the map no longer spells CSS. The pairing is the one
 * those maps carried; the retired `badge-secondary` (inactive, terminated) is `neutral`.
 */
const ACCOUNT_STATUS_BADGE_TONES = Object.freeze({
  active: BADGE_TONE.SUCCESS,
  inactive: BADGE_TONE.NEUTRAL,
  suspended: BADGE_TONE.DANGER,
  invited: BADGE_TONE.INFO,
  terminated: BADGE_TONE.NEUTRAL,
  imported: BADGE_TONE.WARNING,
});

/** Employment status → badge TONE (replaces `EMPLOYMENT_STATUS_BADGE_CLASSES`). */
const EMPLOYMENT_STATUS_BADGE_TONES = Object.freeze({
  ACTIVE: BADGE_TONE.SUCCESS,
  ON_LEAVE: BADGE_TONE.WARNING,
  SUSPENDED: BADGE_TONE.WARNING,
  TERMINATED: BADGE_TONE.NEUTRAL,
});

// ========== TENANT STATUS (`public.companies.status`) ==========

/**
 * A tenant's (company's) lifecycle status — the `public.companies` `chk_status` vocabulary,
 * measured from the constraint itself (pos-api `20251230000001_standardize_tenant_id_format.js`,
 * which widened it to add `trial` and `pending` for the signup activation flow).
 *
 * The same five values are `COMPANY_STATUS` in `@puffin-core/unified-models` and
 * `@puffin-core/middleware` (API tier). They are restated here, not imported, because this
 * package is dual-use and those are API-only; the cross-app `uiApiEnumParity` registry
 * (`status-taxonomy/tenant-status`) holds the three copies and the CHECK together.
 */
const TENANT_STATUS = Object.freeze({
  ACTIVE: 'active',
  TRIAL: 'trial',
  PENDING: 'pending',
  SUSPENDED: 'suspended',
  CANCELLED: 'cancelled',
});

const TENANT_STATUS_VALUES = Object.freeze(Object.values(TENANT_STATUS));

/**
 * The ONE set of LIVE tenant statuses: activated and not suspended or cancelled. `pending` is a tenant still being
 * provisioned (or, before its activation, about to be removed — removal is refused once it leaves `pending`).
 * `@puffin-core/unified-models` `LIVE_COMPANY_STATUSES` (who may log in, which vanity hosts resolve) IS this array, and
 * the sweep discovery (`@puffin-core/platform-admin-routes` `discoverActiveTenantSchemas`) reaches only these tenants
 * for a customers sweep (OBSERVABILITY-SIGNAL-001).
 */
const LIVE_TENANT_STATUSES = Object.freeze([TENANT_STATUS.ACTIVE, TENANT_STATUS.TRIAL]);

/**
 * Tenant status → badge TONE — the ONE map every platform-admin surface reads (Q-MI-25
 * F-DIVERGE-TENANT, lead ruling 2026-09-26). palm-web toned every non-active status WARNING while
 * planner-web and console toned it NEUTRAL; this map tones by MEANING instead: a live tenant is a
 * success, a tenant still starting up (trial, pending activation) is information, a suspended
 * tenant is a problem, and a cancelled one is simply over.
 */
const TENANT_STATUS_BADGE_TONES = Object.freeze({
  [TENANT_STATUS.ACTIVE]: BADGE_TONE.SUCCESS,
  [TENANT_STATUS.TRIAL]: BADGE_TONE.INFO,
  [TENANT_STATUS.PENDING]: BADGE_TONE.INFO,
  [TENANT_STATUS.SUSPENDED]: BADGE_TONE.DANGER,
  [TENANT_STATUS.CANCELLED]: BADGE_TONE.NEUTRAL,
});

// ========== EMPLOYEE-SCHEDULE (shift row) STATUS ==========

/**
 * Committed shift-row lifecycle (`employee_schedules.status`, CHECK-constrained
 * in the DB). PLN-SCHED-LIFECYCLE-001: the amendment layer cancels shifts by
 * STATUS (`canceled` rows are kept as predictability-pay evidence, never
 * deleted), so every cross-app reader of the shared `employee_schedules`
 * table MUST filter to `ACTIVE_EMPLOYEE_SCHEDULE_STATUSES` unless it is
 * explicitly reporting on cancellations. Enforced by the
 * `employeeScheduleStatus` cross-app contract test.
 */
const EMPLOYEE_SCHEDULE_STATUS = Object.freeze({
  SCHEDULED: 'scheduled',
  CONFIRMED: 'confirmed',
  COMPLETED: 'completed',
  CANCELED: 'canceled',
});

/** The statuses a live schedule read should see (everything except canceled). */
const ACTIVE_EMPLOYEE_SCHEDULE_STATUSES = Object.freeze([
  EMPLOYEE_SCHEDULE_STATUS.SCHEDULED,
  EMPLOYEE_SCHEDULE_STATUS.CONFIRMED,
  EMPLOYEE_SCHEDULE_STATUS.COMPLETED,
]);

const EMPLOYEE_SCHEDULE_STATUS_VALUES = Object.freeze(Object.values(EMPLOYEE_SCHEDULE_STATUS));

// ========== SCHEDULE-READINESS BLOCKERS ==========

/**
 * Reasons a team member is not yet fully schedule-ready. Returned by
 * `deriveScheduleReadiness` and rendered as chips in the PlannerPuffin
 * team list.
 */
const SCHEDULE_BLOCKER = Object.freeze({
  TERMINATED: 'terminated',
  ON_LEAVE: 'on_leave',
  SUSPENDED_EMPLOYMENT: 'suspended_employment',
  NOT_INVITED: 'not_invited',
  INVITED: 'invited',
  INACTIVE: 'inactive',
  SUSPENDED_ACCOUNT: 'suspended_account',
  ACCOUNT_TERMINATED: 'account_terminated',
  MISSING_EMAIL: 'missing_email',
  NO_ROLE: 'no_role',
  NO_LOCATION: 'no_location',
});

const SCHEDULE_BLOCKER_LABELS = Object.freeze({
  terminated: 'Terminated',
  on_leave: 'On leave',
  suspended_employment: 'Employment suspended',
  not_invited: 'Not invited',
  invited: 'Invite pending',
  inactive: 'Account inactive',
  suspended_account: 'Account suspended',
  account_terminated: 'Account terminated',
  missing_email: 'Missing email',
  no_role: 'No role',
  no_location: 'No location',
});

// ========== PAYROLL-RUN (payday) STATUS ==========

/**
 * Payroll-run lifecycle (`payday_payroll_runs.status`) — promoted to this
 * SSOT by HTP-001 (2026-07-26). payday-api re-exports it under its legacy
 * local name `PAYROLL_STATUS` (all API call sites unchanged, exactly the
 * `ACCOUNT_STATUS` re-export precedent in its `payrollConstants.js`);
 * payday-web imports it directly. The web copy previously hand-mirrored
 * the enum WRONGLY (missing `calculating`, phantom `processing`) — the
 * drift class this package exists to kill.
 */
const PAYROLL_RUN_STATUS = Object.freeze({
  DRAFT: 'draft',
  HOURS_SYNCED: 'hours_synced',
  CALCULATING: 'calculating',
  CALCULATED: 'calculated',
  SUBMITTED: 'submitted',
  PROCESSED: 'processed',
  PAID: 'paid',
  CANCELLED: 'cancelled',
  FAILED: 'failed',
  // PAYDAY-RUN-LIFECYCLE-001 (owner ruling Q3, "Reset, plus a real Skip"): a regular pay period the processor runs as a
  // $0 payroll. Terminal. A skipped run pays nothing, so it locks no punches: its hours stay visible as unpaid.
  SKIPPED: 'skipped',
});

/**
 * Valid payroll-run status transitions — the state machine consumed by
 * payday-api `PayrollRun.updateStatus` and `PayrollPollingService`.
 * `paid`, `cancelled` and `skipped` are terminal; `failed` may only reset to `draft`.
 * A regular pay period may be SKIPPED only before it is submitted (Q3). A submission is cancelled back to `draft`
 * before the processor's deadline (Q8, "Cancel to deadline, then off-cycle"): the processor reverts it to unprocessed.
 */
const PAYROLL_RUN_STATUS_TRANSITIONS = Object.freeze({
  [PAYROLL_RUN_STATUS.DRAFT]: Object.freeze([
    PAYROLL_RUN_STATUS.HOURS_SYNCED,
    PAYROLL_RUN_STATUS.CANCELLED,
    PAYROLL_RUN_STATUS.SKIPPED,
  ]),
  [PAYROLL_RUN_STATUS.HOURS_SYNCED]: Object.freeze([
    PAYROLL_RUN_STATUS.CALCULATING,
    PAYROLL_RUN_STATUS.DRAFT,
    PAYROLL_RUN_STATUS.CANCELLED,
    PAYROLL_RUN_STATUS.SKIPPED,
  ]),
  [PAYROLL_RUN_STATUS.CALCULATING]: Object.freeze([
    PAYROLL_RUN_STATUS.CALCULATED,
    PAYROLL_RUN_STATUS.FAILED,
  ]),
  [PAYROLL_RUN_STATUS.CALCULATED]: Object.freeze([
    PAYROLL_RUN_STATUS.SUBMITTED,
    PAYROLL_RUN_STATUS.CANCELLED,
    PAYROLL_RUN_STATUS.SKIPPED,
  ]),
  [PAYROLL_RUN_STATUS.SUBMITTED]: Object.freeze([
    PAYROLL_RUN_STATUS.PROCESSED,
    PAYROLL_RUN_STATUS.FAILED,
    PAYROLL_RUN_STATUS.DRAFT,
  ]),
  [PAYROLL_RUN_STATUS.PROCESSED]: Object.freeze([PAYROLL_RUN_STATUS.PAID]),
  [PAYROLL_RUN_STATUS.PAID]: Object.freeze([]),
  [PAYROLL_RUN_STATUS.CANCELLED]: Object.freeze([]),
  [PAYROLL_RUN_STATUS.FAILED]: Object.freeze([PAYROLL_RUN_STATUS.DRAFT]),
  [PAYROLL_RUN_STATUS.SKIPPED]: Object.freeze([]),
});

const PAYROLL_RUN_STATUS_VALUES = Object.freeze(Object.values(PAYROLL_RUN_STATUS));

/**
 * Payroll-run TYPES (`payday_payroll_runs."payrollType"`, CHECK-enforced to exactly these values; PAYDAY-RUN-LIFECYCLE-001).
 * The type decides what a run does to the ecosystem: a regular run locks its pay period's punches and is the period's
 * planner frontier; an off-cycle run (a bonus, a correction) locks nothing; a termination run pays one leaver. Every
 * reader that judges by type reads this ONE vocabulary. payday-api re-exports it as `PAYROLL_TYPE`, and payday-web
 * imports it, never a copy.
 */
const PAYROLL_RUN_TYPE = Object.freeze({
  REGULAR: 'regular',
  OFF_CYCLE: 'off_cycle',
  TERMINATION: 'termination',
});

const PAYROLL_RUN_TYPE_VALUES = Object.freeze(Object.values(PAYROLL_RUN_TYPE));

/**
 * The run types a Cancel applies to (owner ruling Q3, "Reset, plus a real Skip"): an off-cycle or termination run is
 * cancelled by deleting its unprocessed payroll at the processor. A regular pay period is never cancelled; it is reset
 * or skipped. payday-api's cancel route and payday-web's action map both read this list.
 */
const PAYROLL_RUN_CANCELLABLE_TYPES = Object.freeze([
  PAYROLL_RUN_TYPE.OFF_CYCLE,
  PAYROLL_RUN_TYPE.TERMINATION,
]);

// ========== PAYROLL FUNDING STATUS (payday) ==========

/**
 * A payroll run's funding pre-check outcome (PAYDAY-RUN-LIFECYCLE-001; owner ruling "Finish it with Plaid"). payday-api's
 * readiness check answers it and payday-web's badge reads it:
 * - `not_checked` — no balance could be read (no bank connected, the connection needs re-linking, no single funding
 *   account, an unreadable amount). It never blocks a submit, and a submit over it is never "forced".
 * - `sufficient` — the funding account's live balance covers the run.
 * - `shortfall` — it does not; the submit is refused unless overridden.
 */
const PAYROLL_FUNDING_STATUS = Object.freeze({
  NOT_CHECKED: 'not_checked',
  SUFFICIENT: 'sufficient',
  SHORTFALL: 'shortfall',
});

const PAYROLL_FUNDING_STATUS_VALUES = Object.freeze(Object.values(PAYROLL_FUNDING_STATUS));

/**
 * Why a funding pre-check is `not_checked`. payday-web shows each reason in words, so an operator knows what to fix.
 * - `no_bank_connection` — no bank is connected;
 * - `bank_reauthorization_required` — the bank connection needs re-linking;
 * - `no_single_funding_account` — the bank gives no single funding account;
 * - `no_available_balance` — the funding account reports no available balance;
 * - `funding_account_not_usd` — the funding account is not in US dollars;
 * - `amount_unreadable` — an item's gross pay or employer taxes cannot be read (an uncalculated item carries none),
 *   so the cash the run needs is unknown and no balance is judged against it (ZERO-DEFAULT MONEY).
 */
const PAYROLL_FUNDING_NOT_CHECKED_REASON = Object.freeze({
  NO_BANK_CONNECTION: 'no_bank_connection',
  BANK_REAUTHORIZATION_REQUIRED: 'bank_reauthorization_required',
  NO_SINGLE_FUNDING_ACCOUNT: 'no_single_funding_account',
  NO_AVAILABLE_BALANCE: 'no_available_balance',
  FUNDING_ACCOUNT_NOT_USD: 'funding_account_not_usd',
  AMOUNT_UNREADABLE: 'amount_unreadable',
});

const PAYROLL_FUNDING_NOT_CHECKED_REASON_VALUES = Object.freeze(
  Object.values(PAYROLL_FUNDING_NOT_CHECKED_REASON),
);

/** Each outcome's tone (Q-MI-25): an unread balance is neutral — it says nothing about the money, so never danger. */
const PAYROLL_FUNDING_STATUS_BADGE_TONES = Object.freeze({
  [PAYROLL_FUNDING_STATUS.NOT_CHECKED]: BADGE_TONE.NEUTRAL,
  [PAYROLL_FUNDING_STATUS.SUFFICIENT]: BADGE_TONE.SUCCESS,
  [PAYROLL_FUNDING_STATUS.SHORTFALL]: BADGE_TONE.DANGER,
});

// ========== PAYROLL ONBOARDING STAGE (payday) ==========

/**
 * Where a team member stands in onboarding to payroll (`payday_employee_payroll_mappings."providerOnboardingStatus"`,
 * CHECK-enforced to exactly these values; PAYDAY-RUN-LIFECYCLE-001). The payroll processor reports a finer status;
 * its adapter maps that onto these stages, and nothing else in the ecosystem reads the processor's own values. The
 * stages say who acts next:
 * - `not_invited` — an invitation to self-onboard is intended but not sent yet;
 * - `invited` — sent, and the team member has not started (or is past their start date and has not finished);
 * - `onboarding` — information is being entered: by an admin, or by the team member who started;
 * - `pending_review` — the team member finished, and an admin reviews it;
 * - `completed` — fully onboarded and verified: payable.
 */
const PAYROLL_ONBOARDING_STATUS = Object.freeze({
  NOT_INVITED: 'not_invited',
  INVITED: 'invited',
  ONBOARDING: 'onboarding',
  PENDING_REVIEW: 'pending_review',
  COMPLETED: 'completed',
});

const PAYROLL_ONBOARDING_STATUS_VALUES = Object.freeze(Object.values(PAYROLL_ONBOARDING_STATUS));

/**
 * Onboarding stage → badge TONE (`<StatusBadgeCell statusTones={…}>`, Q-MI-25), one entry per stage so a new stage
 * cannot render untoned: waiting on someone is `info`, work in progress `warning`, done `success`.
 */
const PAYROLL_ONBOARDING_STATUS_BADGE_TONES = Object.freeze({
  [PAYROLL_ONBOARDING_STATUS.NOT_INVITED]: BADGE_TONE.NEUTRAL,
  [PAYROLL_ONBOARDING_STATUS.INVITED]: BADGE_TONE.INFO,
  [PAYROLL_ONBOARDING_STATUS.ONBOARDING]: BADGE_TONE.WARNING,
  [PAYROLL_ONBOARDING_STATUS.PENDING_REVIEW]: BADGE_TONE.INFO,
  [PAYROLL_ONBOARDING_STATUS.COMPLETED]: BADGE_TONE.SUCCESS,
});

/**
 * How long a stored onboarding stage reads as current: two of payday's 30-minute employee polls. An older read is
 * still shown, with the time it was read, because one missed poll is normal and two are not.
 */
const PAYROLL_ONBOARDING_STATUS_FRESH_FOR_MS = 2 * 30 * 60 * 1000;

/**
 * Whether a stored onboarding stage means the team member is fully onboarded and payable. The ONE reading of
 * "onboarded" (PAYDAY-RUN-LIFECYCLE-001): the stage carries the fact, so no table keeps a second boolean of it. A
 * stage never read (`null`) is not completed.
 *
 * @param {?string} stage - a `PAYROLL_ONBOARDING_STATUS` value, or `null`
 * @returns {boolean}
 * @throws {TypeError} for any other value: a stage the vocabulary does not hold is never read as "not onboarded"
 */
function isOnboardingCompleted(stage) {
  if (stage !== null && !PAYROLL_ONBOARDING_STATUS_VALUES.includes(stage)) {
    throw new TypeError(
      `isOnboardingCompleted: not a payroll onboarding stage: ${JSON.stringify(stage)}`,
    );
  }
  return stage === PAYROLL_ONBOARDING_STATUS.COMPLETED;
}

// ========== SCHEDULE ABSENCE (call-out) REASON ==========

/**
 * Why a committed shift will not be worked (`planner_schedule_absences.reason`,
 * PLN-SCHED-LIFECYCLE-001). The column is a plain string with no DB CHECK, so
 * this list IS the vocabulary — and it was written out three times, in
 * planner-api's manager route, planner-api's internal call-out receiver and
 * palm-api's call-out relay, with the employee's clients about to make four
 * (UAT-BLOCKERS-2026-09-18). One list now; every route validates against it.
 */
const SCHEDULE_ABSENCE_REASON = Object.freeze({
  SICK: 'sick',
  EMERGENCY: 'emergency',
  PERSONAL: 'personal',
  OTHER: 'other',
  NO_SHOW: 'no_show',
});

const SCHEDULE_ABSENCE_REASON_VALUES = Object.freeze(Object.values(SCHEDULE_ABSENCE_REASON));

/**
 * `planner_excluded_days.source` — why a day is kept out of the record
 * (`20260708120000_create_execution_layer_tables`'s CHECK, widened by
 * `20261001222400_pln_dept_tab_004_excluded_days_chain_scope`).
 *
 * A **closure** is a day the store DID NOT OPEN; an **anomaly** is a day it did open whose demand
 * is not representative (a blizzard, a parade, a register outage). The distinction is load-bearing
 * and asymmetric: the forecast loop excludes BOTH, while only a closure removes real work — it
 * blocks a schedule write, stops a business clock, and (LEAVE-CANON-001 P3c) costs an employee no
 * leave hours, because nobody could have worked it.
 *
 * A **manual** row (PLN-DEPT-TAB-004, owner rulings R1 / R3, D-STORE) is a day an admin excludes
 * from every history read by hand, on Settings ▸ Departments ▸ History Exclusions — CHAIN-WIDE
 * (`locationId` NULL, the one source that may be), never a closure (a CHECK keeps every closure
 * store-scoped, so palm's closure read is unaffected by construction). It joined the ONE register
 * rather than a second table or a settings key: an anomaly at chain scope is the same thing the
 * table already records.
 *
 * Shared rather than planner-local because palm reads these rows through the declared
 * `planner_excluded_days` cross-app surface and must compare on the same literal. A second copy of
 * a CHECK-constrained vocabulary is a second place for it to drift.
 */
const EXCLUDED_DAY_SOURCE = Object.freeze({
  CLOSURE: 'closure',
  ANOMALY: 'anomaly',
  MANUAL: 'manual',
});

const EXCLUDED_DAY_SOURCE_VALUES = Object.freeze(Object.values(EXCLUDED_DAY_SOURCE));

/**
 * The history-exclusion RESOLVER's wire vocabulary (PLN-DEPT-TAB-004 R1 / D-STORE): the register's
 * three stored sources plus ONE derived source. A closed day on the Holiday calendar
 * (`planner_holidays.isClosed`) is never stored as an excluded day — the owner ruled the calendar
 * is presented, not imported — so the resolver merges it in at read time under a name the
 * CHECK-constrained column can never hold. Planner-web's section badges it; palm never sees it.
 */
const HISTORY_EXCLUSION_SOURCE = Object.freeze({
  ...EXCLUDED_DAY_SOURCE,
  HOLIDAY: 'holiday',
});

/**
 * When ONE date is excluded for several reasons the resolver's row reads as the HIGHEST, and lists
 * every reason under `sources[]`: a store that did not open outranks an anomaly it also flagged,
 * which outranks a calendar closure, which outranks a day an admin also excluded by hand — the
 * manual row stays removable underneath the holiday it shadows.
 */
const HISTORY_EXCLUSION_SOURCE_PRECEDENCE = Object.freeze([
  HISTORY_EXCLUSION_SOURCE.CLOSURE,
  HISTORY_EXCLUSION_SOURCE.ANOMALY,
  HISTORY_EXCLUSION_SOURCE.HOLIDAY,
  HISTORY_EXCLUSION_SOURCE.MANUAL,
]);

/**
 * Which resolver sources take a day OUT of history, and which merely say the store was closed
 * (PLN-LY-TWO-GRAINS-001 R1, owner 2026-10-07: last year "needs to be LY actuals, regardless if the
 * store was scheduled to be open or closed … unless a user marks the data as bad and asks it to be
 * excluded").
 *
 * USER sources are a person's verdict on the data — an anomaly a manager flagged, a chain-wide
 * manual exclusion — and zero the day in every sum. COUNTED sources are closed days: a closure the
 * store did not open for, a holiday the calendar closes. What those days sold (usually nothing, now
 * and then a manager's off-day deposit) is history and counts. A resolved row lists EVERY reason
 * under `sources[]`, so a date that is both a closure and a manual exclusion is still a user
 * verdict: the projection reads the list, never the precedence headline. Declared once here so the
 * projection, the Settings section's badges and palm's read agree on the split.
 */
const HISTORY_EXCLUSION_USER_SOURCES = Object.freeze([
  HISTORY_EXCLUSION_SOURCE.ANOMALY,
  HISTORY_EXCLUSION_SOURCE.MANUAL,
]);

const HISTORY_EXCLUSION_COUNTED_SOURCES = Object.freeze([
  HISTORY_EXCLUSION_SOURCE.CLOSURE,
  HISTORY_EXCLUSION_SOURCE.HOLIDAY,
]);

/**
 * Is this resolved row a USER's verdict on the day (PLN-LY-TWO-GRAINS-001 R1)? Reads the row's
 * `sources[]` — the resolver lists every reason a date is out — never the precedence headline
 * `source`: a closure a user also marked bad reads `source: 'closure'` and is still a user verdict.
 * The ONE reading on both tiers: planner-api's projection zeroes exactly these rows, planner-web's
 * department ladder seeds exactly these dates. A row without its list cannot answer the question,
 * so the call is refused rather than read as "not excluded".
 *
 * @param {string[]} sources - The resolved row's `sources[]`.
 * @returns {boolean}
 * @throws {TypeError} When `sources` is not an array.
 */
function isUserHistoryExclusion(sources) {
  if (!Array.isArray(sources)) {
    throw new TypeError('isUserHistoryExclusion reads a resolved row’s sources[] list');
  }
  return sources.some((source) => HISTORY_EXCLUSION_USER_SOURCES.includes(source));
}

/**
 * The basis a historical net-sales read is projected on (PLN-DEPT-TAB-004 R3; the sum rule
 * restated by PLN-LY-TWO-GRAINS-001 R1).
 *
 * `baseline` — what the tills recorded, net of the days a USER marked bad (`HISTORY_EXCLUSION_USER_SOURCES`):
 * such a day counts ZERO in every sum and is marked on its row; a closed day (`…_COUNTED_SOURCES`)
 * keeps its sales and is marked `counted`. Nothing is imputed. The basis every plan is shaped from
 * and every "vs LY" is judged against.
 * `actuals` — what the tills recorded, untouched; the only honest basis for "what did THIS
 * period actually do". A caller states which it wants: the server refuses a read that does not.
 */
const HISTORICAL_BASIS = Object.freeze({
  BASELINE: 'baseline',
  ACTUALS: 'actuals',
});

const HISTORICAL_BASIS_VALUES = Object.freeze(Object.values(HISTORICAL_BASIS));

/**
 * The universe a tenant's day-of-week sales weights were measured over (PLN-LY-TWO-GRAINS-001 R10 —
 * the weights' stored `provenance.basis`, disclosed wherever they render).
 *
 * `all-transactions` — every completed sale in the span, whatever department it sold under. The
 * only read the auto-populate has today, and the right one for a tenant with no Monthly department
 * group.
 * `daily-fact` — the department fact with every Monthly group's departments EXCLUDED, so a
 * month-grain booking (a virtual-mail deposit, a carrier settlement) that lands on one weekday does
 * not shape the week's day curve. Produced once the fact read exists (Phase 3).
 *
 * A weekday pattern is a RATE: a number with no stated universe is the defect R10 names, so the
 * basis rides the weights into settings and onto the screen, on both tiers.
 */
const DOW_WEIGHT_BASIS = Object.freeze({
  ALL_TRANSACTIONS: 'all-transactions',
  DAILY_FACT: 'daily-fact',
});

const DOW_WEIGHT_BASIS_VALUES = Object.freeze(Object.values(DOW_WEIGHT_BASIS));

/**
 * The SALES BASIS marker every grain-aware sales figure carries on the wire (PLN-LY-TWO-GRAINS-001
 * U3; owner rulings R3 / R6 / R12) — `basis: { grain, source, includesMonthly, monthlyDepartmentCount,
 * missingDays, reason? }`. Three vocabularies, each the ONE spelling on both tiers:
 *
 * `SALES_BASIS_GRAIN` — WHAT the figure is (the same two tokens as a plan row's `GRAIN_SCOPE`, so a
 * day surface asks for `day` and reads `day`):
 *   `day` — the DAILY grain: the store's sales LESS its Monthly departments (or the whole store,
 *     when nothing is planned monthly — the whole store IS the Daily grain then);
 *   `all` — every sale, Monthly departments included.
 *
 * `SALES_BASIS_SOURCE` — WHERE it was read:
 *   `transactions` — the ONE net-sales universe over `transactions`;
 *   `fact` — the department fact (`pos_sales_department_daily`) with the Monthly departments left
 *     out, after the fact RECONCILED with the whole-store total on every store-day of the window;
 *   `transactions-fallback` — the whole store read in place of the fact, because the fact could
 *     not be trusted over the window (R12: fall back WITH a marker, never silently).
 *
 * `SALES_BASIS_FALLBACK_REASON` — WHY, when the source is the fallback:
 *   `fact-absent` — a store-day the store sold on has no fact row (the rollup is off, or the
 *     backfill has not reached it);
 *   `fact-stale` — the fact disagrees with the whole-store total by a cent or more on a store-day
 *     (Phase 0 M1: the nightly rewrites only the trailing week).
 */
const SALES_BASIS_GRAIN = Object.freeze({
  DAY: 'day',
  ALL: 'all',
});

const SALES_BASIS_GRAIN_VALUES = Object.freeze(Object.values(SALES_BASIS_GRAIN));

const SALES_BASIS_SOURCE = Object.freeze({
  TRANSACTIONS: 'transactions',
  FACT: 'fact',
  TRANSACTIONS_FALLBACK: 'transactions-fallback',
});

const SALES_BASIS_SOURCE_VALUES = Object.freeze(Object.values(SALES_BASIS_SOURCE));

const SALES_BASIS_FALLBACK_REASON = Object.freeze({
  FACT_ABSENT: 'fact-absent',
  FACT_STALE: 'fact-stale',
});

const SALES_BASIS_FALLBACK_REASON_VALUES = Object.freeze(
  Object.values(SALES_BASIS_FALLBACK_REASON),
);

/**
 * Where a store day's sales plan stands, as pos's `GET /v1/dashboard/daily-goals` answers it and
 * palm's manager card reads it (LEAVE-CANON-001 R14 G6; PALM-PHONE-UAT-002 W3, owner ruling R-PU2-5):
 * set, not set, or unreadable right now. ONE vocabulary — pos-api answered it and pos-web mirrored it
 * by hand until W3 moved both onto this constant.
 */
const DAY_PLAN_STATUS = Object.freeze({
  PLANNED: 'planned',
  UNPLANNED: 'unplanned',
  UNAVAILABLE: 'unavailable',
});

const DAY_PLAN_STATUS_VALUES = Object.freeze(Object.values(DAY_PLAN_STATUS));

/**
 * WHY a day's goal tile shows every sale instead of the Daily grain (PLN-LY-TWO-GRAINS-001 P6, R12:
 * a fallback is said, never silent):
 *   `planner_unavailable` — planner could not be read, so the Monthly set is unknown;
 *   `grain_unavailable` — planner answered outside the contract (another grain, an untrustworthy set);
 *   `grain_unreconciled` — the live attribution and the store's net sales disagreed about one window
 *     read at one instant (logged at `error` by the reader).
 */
const DAILY_GOALS_FALLBACK_REASON = Object.freeze({
  PLANNER_UNAVAILABLE: 'planner_unavailable',
  GRAIN_UNAVAILABLE: 'grain_unavailable',
  GRAIN_UNRECONCILED: 'grain_unreconciled',
});

const DAILY_GOALS_FALLBACK_REASON_VALUES = Object.freeze(
  Object.values(DAILY_GOALS_FALLBACK_REASON),
);

/**
 * The ONE unit an hourly traffic profile is measured in (PLANNER-CANVAS-ONE-GRADE-001 U7c; owner
 * ruling R-OG-5 "the demand line is ONE series in transactions … the hourly profile's unit is
 * recorded and standardised on transactions"). The profile's stored `metadata.unit` carries it, the
 * auto-populate answers it, the save REQUIRES it, and the demand curve names its series by it; a
 * `sales` profile is RETIRED, so this is a vocabulary of one — declared once so planner-api's
 * schema and planner-web's save never spell it apart.
 */
const HOURLY_WEIGHTS_UNIT = 'transactions';

/**
 * Where a day's MONTHLY department set came from, on the wire between planner-api's internal
 * day-target route and pos-api's Daily Goals tile (PLN-LY-TWO-GRAINS-001 U4a / P6; owner ruling R9):
 *   `plan` — the plan's own frozen Monthly bookings whose month covers the day (the set the Daily
 *     figure was BUILT against);
 *   `profiles` — the live planning profiles' Monthly departments, when no plan covers the day.
 * One vocabulary on both tiers: pos refuses an answer that names a source it does not know.
 */
const MONTHLY_SET_SOURCE = Object.freeze({
  PLAN: 'plan',
  PROFILES: 'profiles',
});

const MONTHLY_SET_SOURCE_VALUES = Object.freeze(Object.values(MONTHLY_SET_SOURCE));

/**
 * The planning GRAIN a department, a department group or a plan item is planned at (PLN-DEPT-GRAIN-001,
 * owner rulings G-5 / G-7; promoted here by PLN-LY-TWO-GRAINS-001 P7). "Monthly" means STRUCTURALLY
 * PERIODIC — one instrument a month (a virtual-mail deposit, a carrier settlement) — never "low unit
 * demand". The two values mirror planner's three DB CHECKs (`pln_dgr_grain_check`, `pln_spi_grain_check`,
 * `pln_dpp_grain_check`) and are what `planner_department_planning_profiles.grain` holds — the column
 * pos-api reads through the cross-app surface for the Daily Sales report's Daily-grain column (R9), so
 * the token lives ONCE, here, and planner's `constants/planningGrain` re-exports it byte-equal.
 */
const PLANNING_GRAIN = Object.freeze({
  DAY: 'day',
  MONTH: 'month',
});

const PLANNING_GRAIN_VALUES = Object.freeze(Object.values(PLANNING_GRAIN));

/**
 * The provenance a forecast-accuracy record is scored under (PLN-LY-TWO-GRAINS-001 U4a; owner ruling R15
 * "accuracy persists its provenance", R35 for the one rule): `planner_forecast_accuracy.scoringExclusions`
 * and `.salesBasis`, each CHECK-backed by the migration (`20261006100750`). The vocabulary lives here,
 * not in planner-api, because THREE readers hold it: planner-api's migration, writer and validator (its
 * `constants/forecastAccuracy` re-exports these byte-equal), and the cross-app seed factory — a test
 * HELPER, which `api/no-cross-app-imports` rightly keeps out of app code.
 *   - scoring rule: `none` — every planned day scored; `user` — the days a USER marked bad (anomaly,
 *     manual) dropped from both sides; closed days score as they were (R35).
 *   - sales basis: `day` — the Daily grain, the department fact reconciled (or the whole store when
 *     nothing is planned monthly); `day-fallback` — the Daily grain was asked for and the fact could not
 *     be trusted, so the whole store was read, marked (R6 / R12).
 */
const FORECAST_SCORING_EXCLUSIONS = Object.freeze({
  NONE: 'none',
  USER: 'user',
});

const FORECAST_SCORING_EXCLUSIONS_VALUES = Object.freeze(
  Object.values(FORECAST_SCORING_EXCLUSIONS),
);

const FORECAST_ACCURACY_SALES_BASIS = Object.freeze({
  DAY: 'day',
  DAY_FALLBACK: 'day-fallback',
});

const FORECAST_ACCURACY_SALES_BASIS_VALUES = Object.freeze(
  Object.values(FORECAST_ACCURACY_SALES_BASIS),
);

/**
 * PLN-PERIOD-LADDER-001 (R-6): goal-attainment verdict on rollup buckets. A NOMINAL state against the
 * tenant's variance threshold — deliberately distinct from continuous variance (which stays a number).
 * Horizon-relative: a bucket with no elapsed days or zero target-to-date is NOT_EVALUATED, never "at risk"
 * — the future is not late (mirrors the null-not-0 honesty rule in SalesPlanningService.getVsActual).
 *
 * Promoted here from planner-api's `constants/statuses` by PLANNER-PERFORMANCE-PAGE-001 U1 (D-3): the
 * performance page renders the verdict in words and the UI↔API parity registry pins the web's label map
 * against THIS constant, and a web app may never import an app. planner-api re-exports it byte-equal.
 */
const ATTAINMENT_STATUS = Object.freeze({
  ON_TRACK: 'on_track',
  AT_RISK: 'at_risk',
  OFF_TRACK: 'off_track',
  NOT_EVALUATED: 'not_evaluated',
});

const ATTAINMENT_STATUS_VALUES = Object.freeze(Object.values(ATTAINMENT_STATUS));

/**
 * PLN-PERIOD-LADDER-001 LY follow-up: why a rollup bucket's `lastYear` leg is withheld
 * (`comparable: false`). Machine-readable and NAMED — the doctrine is refuse-never-guess: an ill-defined
 * comparison says which way it is ill-defined, never a silently-wrong number. Scope-level reasons (the
 * first three) repeat on every bucket; the last two are per-bucket. The wire carries the code itself
 * (every value equals its key). Promoted here with `ATTAINMENT_STATUS` (PLANNER-PERFORMANCE-PAGE-001 U1).
 */
const ROLLUP_LY_REASON = Object.freeze({
  // The comp-aligned LY window is not governed by the same calendar rule as the requested range
  // (cross-era LY, pre-history, or an unreadable LY era ledger).
  LY_CROSS_ERA: 'LY_CROSS_ERA',
  // Store scope: the store was not open for the whole comp span (LY window start → TY elapsed end),
  // so its year-over-year figure would be fiction.
  NON_COMP_STORE: 'NON_COMP_STORE',
  // Group scope: zero comp stores in scope (or an empty scope) — comp-sales practice restricts BOTH
  // sides to comp stores, and there are none.
  NO_COMP_STORES: 'NO_COMP_STORES',
  // The bucket's entire elapsed window is a 53rd week with no LY counterpart (Method A: the extra
  // week has nothing honest to compare against).
  W53_NO_LY_COUNTERPART: 'W53_NO_LY_COUNTERPART',
  // Nothing has elapsed in this bucket yet — there is no window to compare.
  NO_ELAPSED_DAYS: 'NO_ELAPSED_DAYS',
});

const ROLLUP_LY_REASON_VALUES = Object.freeze(Object.values(ROLLUP_LY_REASON));

/**
 * PLANNER-PERFORMANCE-PAGE-001 (owner ruling R-PP-4 "Both scheduled and punched"): why a
 * performance-ladder bucket's LABOR leg withholds a figure — the sibling of `ROLLUP_LY_REASON` for the
 * labor side. A figure that would be fake is `null` WITH its reason; a measured zero stays `0`. The API
 * writes the code, the web renders it in words, and the parity registry pins the label map against it.
 */
const PERFORMANCE_LABOR_REASON = Object.freeze({
  // Nothing has elapsed in the bucket yet — no punched hours TO DATE can exist.
  NO_ELAPSED_DAYS: 'NO_ELAPSED_DAYS',
  // No ACTIVE labor budget covers any day of the bucket — no budget figure, no hours variance.
  NO_LABOR_BUDGET: 'NO_LABOR_BUDGET',
  // No published shift lies in the bucket — no scheduled hours.
  NO_SCHEDULE: 'NO_SCHEDULE',
  // The tenant runs without labor planning (LABOR-PLANNING-SWITCH-001 A7, owner ruling "hide history
  // uniformly"): the budget leg is withheld on EVERY bucket, whatever rows exist, and the words say
  // "planning off" — never "no budget", which would send a manager to author one.
  LABOR_PLANNING_DISABLED: 'LABOR_PLANNING_DISABLED',
});

const PERFORMANCE_LABOR_REASON_VALUES = Object.freeze(Object.values(PERFORMANCE_LABOR_REASON));

/**
 * WHY a report withholds its MONTHLY actual (PLN-LY-TWO-GRAINS-001 U4b; owner ruling R16, with R12):
 * a Monthly department's smallest grain is the MONTH, so a free-window report states the Monthly
 * actual (every sale minus the Daily grain) only over WHOLE fiscal months under the tenant's
 * calendar rule — and only when the Daily grain came from the fact, because a whole-store fallback
 * cannot split the store. The note a `null` carries, on both tiers:
 *   `partial-months` — the window is not whole fiscal months (an era boundary inside it counts);
 *   `fact-absent` / `fact-stale` — the reader's fallback reason, verbatim (the same tokens as
 *     `SALES_BASIS_FALLBACK_REASON`, so a surface translates one vocabulary).
 */
const MONTHLY_ACTUAL_NOTE = Object.freeze({
  PARTIAL_MONTHS: 'partial-months',
  FACT_ABSENT: SALES_BASIS_FALLBACK_REASON.FACT_ABSENT,
  FACT_STALE: SALES_BASIS_FALLBACK_REASON.FACT_STALE,
});

const MONTHLY_ACTUAL_NOTE_VALUES = Object.freeze(Object.values(MONTHLY_ACTUAL_NOTE));

/**
 * The KIND of a comparison anomaly (PLN-LY-TWO-GRAINS-001 U4b): a prior-year day whose sales sit
 * beyond the series' threshold ABOVE the mean (`high`) or BELOW it (`low`). The server names the
 * kind and the day's top departments from the fact; the web translates the kind
 * (`sales.anomaly.note.high|low`) — there is no English sentence on the wire guessing at a cause.
 */
const ANOMALY_TYPE = Object.freeze({
  HIGH: 'high',
  LOW: 'low',
});

const ANOMALY_TYPE_VALUES = Object.freeze(Object.values(ANOMALY_TYPE));

/**
 * WHY a day is CLOSED (PLN-LY-TWO-GRAINS-001 U5; owner ruling R2 and the S07 closed-Sunday finding,
 * Q-LT-11; widened to the close-day act by PLANNER-CANVAS-UAT-002 C1, owner ruling R-UAT2-10 "One
 * rule for all closed days, labor allowed, graded neutral") — the `kind` on every closed-day
 * disclosure a plan write answers with (`meta.closedDays[]`), on every entry of the ONE closed-date
 * read (`ClosedDayService.closedDatesFor`), and on every closed day the schedule canvas is told about:
 *   `holiday` — a `planner_holidays` row marked closed names the date (the entry carries `name`);
 *   `weekday` — the store's `location_hours` close that weekday, at EVERY store of the scope (the
 *     entry carries `dayOfWeek`, 0 = Sunday … 6 = Saturday);
 *   `closure` — the close-day act wrote a `planner_excluded_days` row with `source = 'closure'` for
 *     the date, at EVERY store of the scope (the entry carries the act's `reason`, which may be
 *     null). The token is `EXCLUDED_DAY_SOURCE.CLOSURE`'s own, so no reader translates between the
 *     register's source and the closed day's kind.
 * planner-web's landing notice reads the kind to say "Thanksgiving" or "Sundays"; when kinds collide
 * on one date the more specific statement wins: a closure outranks a holiday row, which outranks the
 * weekday.
 */
const CLOSED_DAY_KIND = Object.freeze({
  HOLIDAY: 'holiday',
  WEEKDAY: 'weekday',
  CLOSURE: 'closure',
});

const CLOSED_DAY_KIND_VALUES = Object.freeze(Object.values(CLOSED_DAY_KIND));

/**
 * `palm_time_off_requests.dayPortion` — how much of a DAY a leave request covers
 * (LEAVE-CANON-001 P3d; the `palm_time_off_requests_day_portion_check` CHECK).
 *
 * A half day is the most-used request type in retail, and until now the table could only express
 * whole days: an employee needing a morning off filed a full day and lost four hours of balance.
 *
 * `partial` is the only value that carries a window. The other three are priced as a FRACTION of
 * whatever that day was already going to cost this employee — which is why the fraction lives here
 * beside the value rather than in a caller: a half day is half the day by definition, not by
 * policy. `partial` has a `null` fraction on purpose, because it is priced from its own
 * `startTime`/`endTime` instead; a caller that forgets the distinction gets `null`, not a silent 1.
 *
 * Shared rather than palm-local because palm-web renders the selector and palm-api validates it,
 * and a closed vocabulary spelled twice is a vocabulary that drifts.
 */
const DAY_PORTION = Object.freeze({
  FULL: 'full',
  FIRST_HALF: 'first_half',
  SECOND_HALF: 'second_half',
  PARTIAL: 'partial',
});

const DAY_PORTION_VALUES = Object.freeze(Object.values(DAY_PORTION));

/** The column default, and what a request that names no portion means. */
const DEFAULT_DAY_PORTION = DAY_PORTION.FULL;

/**
 * The share of a day's working time each portion costs. `partial` is `null` — it is priced from
 * its own window, not from a fraction.
 */
const DAY_PORTION_FRACTION = Object.freeze({
  [DAY_PORTION.FULL]: 1,
  [DAY_PORTION.FIRST_HALF]: 0.5,
  [DAY_PORTION.SECOND_HALF]: 0.5,
  [DAY_PORTION.PARTIAL]: null,
});

/**
 * Does this portion cover the whole day? Phrased positively so it never reads as a near-synonym of
 * the `partial` VALUE — "not full" and "is `partial`" are different questions and both get asked.
 *
 * @param {string} dayPortion
 * @returns {boolean}
 */
function dayPortionCoversWholeDay(dayPortion) {
  return dayPortion === DAY_PORTION.FULL;
}

/**
 * Must this portion carry an explicit `startTime`/`endTime` window? True for `partial` alone — the
 * halves are implied by the day, so times on one of them are a contradiction the DB CHECK refuses.
 *
 * @param {string} dayPortion
 * @returns {boolean}
 */
function dayPortionRequiresWindow(dayPortion) {
  return dayPortion === DAY_PORTION.PARTIAL;
}

/**
 * The reasons an employee may give when they call out of their OWN shift. A
 * no-show is a manager's finding about a shift nobody called out of — never
 * something an employee reports about themselves.
 */
const SELF_REPORTED_ABSENCE_REASONS = Object.freeze(
  SCHEDULE_ABSENCE_REASON_VALUES.filter((reason) => reason !== SCHEDULE_ABSENCE_REASON.NO_SHOW),
);

/**
 * `palm_availability_patterns.availabilityType` / `palm_availability_exceptions.availabilityType`
 * — what a stretch of an employee's week means for scheduling (LEAVE-CANON-001 9p; the
 * `20260301000001_create_palm_availability` CHECKs).
 *
 * `unavailable` is the only hard NO; `not_preferred` is a scheduling signal a manager may override.
 * Shared because palm-api validates it while palm-web, planner-api and planner-web read it, and it
 * had already drifted: palm-web's form offered `time-off`, which nothing accepts, and lacked
 * `not_preferred`, which the route does. The VALUES order is the order a picker offers them.
 */
const AVAILABILITY_TYPE = Object.freeze({
  AVAILABLE: 'available',
  PREFERRED: 'preferred',
  NOT_PREFERRED: 'not_preferred',
  UNAVAILABLE: 'unavailable',
});

const AVAILABILITY_TYPE_VALUES = Object.freeze(Object.values(AVAILABILITY_TYPE));

/**
 * `palm_time_off_requests.status` — where a leave request stands (LEAVE-CANON-001; shared here
 * since 9y). palm owns the rows and moves them through `TimeOffRequest.STATUS_MACHINE`; planner
 * DECIDES requests and reads the decided rows; palm-web and palm-native word every status.
 *
 * `partially_approved` is terminal and belongs to a PARENT only: its decisions live on the child
 * rows (`splitFromRequestId`), which carry `approved` or `rejected`.
 *
 * It lived in `@puffin-core/unified-models`, which the clients cannot import, so each client kept
 * its own copy, and both lacked `partially_approved`: a partially approved request showed the raw
 * enum. The readings built on it (which statuses are OPEN, which STAND) stay with their consumers
 * in `unified-models`.
 */
const TIME_OFF_REQUEST_STATUS = Object.freeze({
  SUBMITTED: 'submitted',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  CANCELLED: 'cancelled',
  PARTIALLY_APPROVED: 'partially_approved',
});

const TIME_OFF_REQUEST_STATUS_VALUES = Object.freeze(Object.values(TIME_OFF_REQUEST_STATUS));

/**
 * The lifecycle of an employee's ask to withdraw APPROVED leave — `palm_time_off_withdrawals.status`
 * (LEAVE-CANON-001 R14, D-R14-2, owner ruling R-68). palm owns the rows and moves them through
 * `TimeOffWithdrawal.STATUS_MACHINE`; planner's approvals queue lists the open ones and answers them;
 * palm-web and palm-native word every status on the employee's card.
 *
 * `submitted` is the only open state. The four closes are final:
 *   - `granted` — an approver took the leave back (the hours returned in the same transaction);
 *   - `declined` — an approver kept the leave, and said why;
 *   - `retracted` — the employee took the ask back;
 *   - `lapsed` — the leave started with the ask unanswered.
 */
const TIME_OFF_WITHDRAWAL_STATUS = Object.freeze({
  SUBMITTED: 'submitted',
  GRANTED: 'granted',
  DECLINED: 'declined',
  RETRACTED: 'retracted',
  LAPSED: 'lapsed',
});

const TIME_OFF_WITHDRAWAL_STATUS_VALUES = Object.freeze(Object.values(TIME_OFF_WITHDRAWAL_STATUS));

/**
 * The lifecycle of a punch an employee's device could not record — `palm_offline_punch_reports.status`
 * (PALM-OFFLINE-PUNCH-SYNC-001; owner rulings Q-POS-2 "Don't record; manager list" and Q-POS-3 "Timecards approval").
 * palm owns the rows (the device reports one when the server refuses a kept punch or it waited too long); pos-web's
 * Timecards approval lists the open ones for the store's managers, who close each.
 *
 * `open` is the only open state. The three closes are final:
 *   - `resolved` — a manager recorded the time another way (a manual punch, or a payroll adjustment);
 *   - `dismissed` — a manager decided nothing is owed, and said why;
 *   - `recorded` — the employee's own device recorded the punch after the report was filed (it was still being sent:
 *     the fourth audit's F5 and G3). No manager closed it, so it carries no `resolvedBy`.
 */
const OFFLINE_PUNCH_REPORT_STATUS = Object.freeze({
  OPEN: 'open',
  RESOLVED: 'resolved',
  DISMISSED: 'dismissed',
  RECORDED: 'recorded',
});

const OFFLINE_PUNCH_REPORT_STATUS_VALUES = Object.freeze(
  Object.values(OFFLINE_PUNCH_REPORT_STATUS),
);

/**
 * Who closed a clock-out (`time_punches.closureSource`, PALM-PHONE-UAT-002 D2b; owner rulings R-PU2-1 / R-PU2-7 /
 * R-PU2-8).
 *
 * - `employee`: the person clocked out at the clock (the default; every row before the column existed).
 * - `auto_stale`: the punch was closed AT the tenant's max-shift cap (`validation.max_shift_hours`) because it had run
 *   past it — written by palm-api's clock-out and clock-in over a punch past the cap, and by pos-api's stale-punch
 *   sweep. Such a clock-out is pending like any other, so pos-api's Timecards queue lists it for a person, but the
 *   payroll-cutoff sweep never auto-approves it: a manager confirms or corrects it first (palm's open-shift sweep files
 *   the manager list's flag for it, `OFFLINE_PUNCH_REPORT_KIND.STALE_OPEN_SHIFT`).
 */
const PUNCH_CLOSURE_SOURCE = Object.freeze({
  EMPLOYEE: 'employee',
  AUTO_STALE: 'auto_stale',
});

const PUNCH_CLOSURE_SOURCE_VALUES = Object.freeze(Object.values(PUNCH_CLOSURE_SOURCE));

/**
 * Which KIND of report a `palm_offline_punch_reports` row is (PALM-PHONE-UAT-001 C4; its meaning set by
 * PALM-PHONE-UAT-002 R-PU2-8 / R-PU2-11):
 *   - `device_refusal` — a punch the employee's device could not record (reported by the device, keyed by the
 *     punch's `clientEventId`);
 *   - `stale_open_shift` — a clock-in the max-shift cap CLOSED (its clock-out's `closureSource` is `auto_stale`),
 *     flagged by palm's open-shift sweep for the store's managers to review (keyed by the clock-in's `punchId`).
 * A manager resolves or dismisses either.
 */
const OFFLINE_PUNCH_REPORT_KIND = Object.freeze({
  DEVICE_REFUSAL: 'device_refusal',
  STALE_OPEN_SHIFT: 'stale_open_shift',
});

const OFFLINE_PUNCH_REPORT_KIND_VALUES = Object.freeze(Object.values(OFFLINE_PUNCH_REPORT_KIND));

/**
 * Team invitation status (`invitations.status`; INVITATIONS-ONE-PATH D3d-2). An invitation is `pending` until it is
 * accepted, withdrawn (`revoked`) or lapses (`expired` — recorded by the reminder job once its cadence has nothing more
 * to send). It lived in `@puffin-core/middleware`, which the web apps cannot import, so the Invitations page could not
 * name the statuses it filters and badges without a second copy.
 */
const INVITATION_STATUS = Object.freeze({
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  EXPIRED: 'expired',
  REVOKED: 'revoked',
});

const INVITATION_STATUS_VALUES = Object.freeze(Object.values(INVITATION_STATUS));

/**
 * The invitation statuses that still stand for their invitee — not yet accepted, not revoked. An EXPIRED invitation is
 * outstanding: its link no longer works, but it is the one a resend renews and a revoke withdraws, and a new invitation
 * for the same person is refused while it stands (INVITATIONS-ONE-PATH D3d, RLT-Q34). invite-core's lifecycle writes
 * guard on it; the Invitations page offers resend / revoke on exactly these rows.
 */
const OUTSTANDING_INVITATION_STATUSES = Object.freeze([
  INVITATION_STATUS.PENDING,
  INVITATION_STATUS.EXPIRED,
]);

// A tenant's platform subscription status: defined in the leaf `./subscriptionStatus.js` (served as
// the `./subscription-status` subpath).
const {
  SUBSCRIPTION_STATUS,
  SUBSCRIPTION_STATUS_VALUES,
  BILLING_ATTENTION_SUBSCRIPTION_STATUSES,
  SUBSCRIPTION_STATUS_BADGE_TONES,
  SUBSCRIPTION_BILLING_MODE,
  SUBSCRIPTION_BILLING_MODE_VALUES,
  TRIAL_PERIOD_DAYS,
} = require('./subscriptionStatus');

// A PortPuffin mailbox holder's subscription status and plan tier: defined in the leaf `./mailboxHolderSubscription.js`
// (served as the `./mailbox-holder-subscription` subpath; CUSTOMER-ERASURE-REACH step 2).
const {
  MAILBOX_HOLDER_SUBSCRIPTION_STATUS,
  MAILBOX_HOLDER_SUBSCRIPTION_STATUS_VALUES,
  MAILBOX_HOLDER_PLAN_TIER,
  MAILBOX_HOLDER_PLAN_TIER_VALUES,
} = require('./mailboxHolderSubscription');

/**
 * Every NAMED warning a time-off submission can carry (LEAVE-CANON-001 P3a / P3b; shared since
 * Phase 9 unit 9j-2). palm-api builds each `{ code, message, details }`; palm-web and palm-native word
 * each code from ONE table (`@puffin-core/palm-shared` `timeOffWords`), keyed by these values.
 */
const LEAVE_WARNING_CODES = Object.freeze({
  BALANCE_BELOW_ZERO: 'LEAVE_BALANCE_BELOW_ZERO',
  COVERAGE_LOW: 'LEAVE_COVERAGE_LOW',
  REQUESTS_OVERLAP: 'LEAVE_REQUESTS_OVERLAP',
  // LEAVE-CANON-001 R14 C1 (owner ruling R-69) — the tenant's per-role cap: colleagues holding the
  // requester's role already off over the same days.
  ROLE_REQUESTS_OVERLAP: 'LEAVE_ROLE_REQUESTS_OVERLAP',
  COVERAGE_UNCHECKED: 'LEAVE_COVERAGE_UNCHECKED',
  BLACKOUT_PERIOD: 'LEAVE_BLACKOUT_PERIOD_WARNING',
  // LEAVE-CANON-001 P3b — the tenant's absence policy.
  HORIZON_EXCEEDED: 'LEAVE_HORIZON_EXCEEDED',
  POLICY_UNCHECKED: 'LEAVE_POLICY_UNCHECKED',
  // Owner ruling R-18 — less notice than the leave type asks for (`minAdvanceNoticeDays`).
  ADVANCE_NOTICE_SHORT: 'LEAVE_ADVANCE_NOTICE_SHORT',
  // LEAVE-CANON-001 R13 W-2 — the type's waiting period could not be judged: no hire date on record.
  WAITING_PERIOD_UNCHECKED: 'LEAVE_WAITING_PERIOD_UNCHECKED',
});

// ========== B2B PARTNER STATUS (purchase `partners.status`) ==========
// Defined in the leaf `./partnerStatus.js` (served as the `./partner-status` subpath).
const { PARTNER_STATUS, PARTNER_STATUS_VALUES } = require('./partnerStatus');

module.exports = {
  PARTNER_STATUS,
  PARTNER_STATUS_VALUES,
  AVAILABILITY_TYPE,
  AVAILABILITY_TYPE_VALUES,
  TIME_OFF_REQUEST_STATUS,
  TIME_OFF_REQUEST_STATUS_VALUES,
  TIME_OFF_WITHDRAWAL_STATUS,
  TIME_OFF_WITHDRAWAL_STATUS_VALUES,
  OFFLINE_PUNCH_REPORT_STATUS,
  OFFLINE_PUNCH_REPORT_STATUS_VALUES,
  PUNCH_CLOSURE_SOURCE,
  PUNCH_CLOSURE_SOURCE_VALUES,
  OFFLINE_PUNCH_REPORT_KIND,
  OFFLINE_PUNCH_REPORT_KIND_VALUES,
  INVITATION_STATUS,
  INVITATION_STATUS_VALUES,
  OUTSTANDING_INVITATION_STATUSES,
  MAILBOX_HOLDER_SUBSCRIPTION_STATUS,
  MAILBOX_HOLDER_SUBSCRIPTION_STATUS_VALUES,
  MAILBOX_HOLDER_PLAN_TIER,
  MAILBOX_HOLDER_PLAN_TIER_VALUES,
  SUBSCRIPTION_STATUS,
  SUBSCRIPTION_STATUS_VALUES,
  BILLING_ATTENTION_SUBSCRIPTION_STATUSES,
  SUBSCRIPTION_STATUS_BADGE_TONES,
  SUBSCRIPTION_BILLING_MODE,
  SUBSCRIPTION_BILLING_MODE_VALUES,
  TRIAL_PERIOD_DAYS,
  LEAVE_WARNING_CODES,
  DAY_PORTION,
  DAY_PORTION_VALUES,
  DAY_PORTION_FRACTION,
  DEFAULT_DAY_PORTION,
  dayPortionCoversWholeDay,
  dayPortionRequiresWindow,
  EXCLUDED_DAY_SOURCE,
  EXCLUDED_DAY_SOURCE_VALUES,
  HISTORY_EXCLUSION_SOURCE,
  HISTORY_EXCLUSION_SOURCE_PRECEDENCE,
  HISTORY_EXCLUSION_USER_SOURCES,
  HISTORY_EXCLUSION_COUNTED_SOURCES,
  isUserHistoryExclusion,
  HISTORICAL_BASIS,
  HISTORICAL_BASIS_VALUES,
  DOW_WEIGHT_BASIS,
  DOW_WEIGHT_BASIS_VALUES,
  SALES_BASIS_GRAIN,
  SALES_BASIS_GRAIN_VALUES,
  SALES_BASIS_SOURCE,
  SALES_BASIS_SOURCE_VALUES,
  SALES_BASIS_FALLBACK_REASON,
  SALES_BASIS_FALLBACK_REASON_VALUES,
  DAY_PLAN_STATUS,
  DAY_PLAN_STATUS_VALUES,
  DAILY_GOALS_FALLBACK_REASON,
  DAILY_GOALS_FALLBACK_REASON_VALUES,
  HOURLY_WEIGHTS_UNIT,
  MONTHLY_SET_SOURCE,
  MONTHLY_SET_SOURCE_VALUES,
  PLANNING_GRAIN,
  PLANNING_GRAIN_VALUES,
  FORECAST_SCORING_EXCLUSIONS,
  FORECAST_SCORING_EXCLUSIONS_VALUES,
  FORECAST_ACCURACY_SALES_BASIS,
  FORECAST_ACCURACY_SALES_BASIS_VALUES,
  ATTAINMENT_STATUS,
  ATTAINMENT_STATUS_VALUES,
  ROLLUP_LY_REASON,
  ROLLUP_LY_REASON_VALUES,
  PERFORMANCE_LABOR_REASON,
  PERFORMANCE_LABOR_REASON_VALUES,
  MONTHLY_ACTUAL_NOTE,
  MONTHLY_ACTUAL_NOTE_VALUES,
  ANOMALY_TYPE,
  ANOMALY_TYPE_VALUES,
  CLOSED_DAY_KIND,
  CLOSED_DAY_KIND_VALUES,
  ACCOUNT_STATUS,
  EMPLOYMENT_STATUS,
  ACCOUNT_STATUS_VALUES,
  EMPLOYMENT_STATUS_VALUES,
  DEFAULT_ACCOUNT_STATUS,
  DEFAULT_EMPLOYMENT_STATUS,
  EMPLOYMENT_TYPE,
  EMPLOYMENT_TYPE_VALUES,
  DEFAULT_EMPLOYMENT_TYPE,
  PAY_TYPE,
  PAY_TYPE_VALUES,
  TERMINATION_REASON,
  TERMINATION_REASON_VALUES,
  UNACCEPTED_ACCOUNT_STATUSES,
  ACCOUNT_UNDO_OUTCOME,
  ACCOUNT_UNDO_OUTCOME_VALUES,
  ACCOUNT_DEACTIVATION_REFUSAL,
  ACCOUNT_DEACTIVATION_REFUSAL_VALUES,
  ACCOUNT_DEACTIVATION_SYSTEM_ACTOR,
  ACCOUNT_DEACTIVATION_SYSTEM_ACTOR_VALUES,
  ACCOUNT_REACTIVATION_REFUSAL,
  ACCOUNT_REACTIVATION_REFUSAL_VALUES,
  ACCOUNT_PASSWORD_RESET_REFUSAL,
  ACCOUNT_PASSWORD_RESET_REFUSAL_VALUES,
  ACCOUNT_AUTHENTICATION_RESET_REFUSAL,
  ACCOUNT_AUTHENTICATION_RESET_REFUSAL_VALUES,
  CALENDAR_SYNC_REVOKE_REFUSAL,
  CALENDAR_SYNC_REVOKE_REFUSAL_VALUES,
  ACCOUNT_ACTIVATION_SYSTEM_ACTOR,
  ACCOUNT_ACTIVATION_SYSTEM_ACTOR_VALUES,
  PLATFORM_OWNER_RESTORE_REFUSAL,
  PLATFORM_OWNER_RESTORE_REFUSAL_VALUES,
  RESTORABLE_OWNER_ACCOUNT_STATUSES,
  ROLE_CHANGE_REFUSAL,
  ROLE_CHANGE_REFUSAL_VALUES,
  SCHEDULABLE_PREDICATE,
  ACCOUNT_STATUS_LABELS,
  EMPLOYMENT_STATUS_LABELS,
  ACCOUNT_STATUS_BADGE_TONES,
  EMPLOYMENT_STATUS_BADGE_TONES,
  TENANT_STATUS,
  TENANT_STATUS_VALUES,
  LIVE_TENANT_STATUSES,
  TENANT_STATUS_BADGE_TONES,
  SCHEDULE_BLOCKER,
  SCHEDULE_BLOCKER_LABELS,
  EMPLOYEE_SCHEDULE_STATUS,
  ACTIVE_EMPLOYEE_SCHEDULE_STATUSES,
  EMPLOYEE_SCHEDULE_STATUS_VALUES,
  PAYROLL_RUN_STATUS,
  PAYROLL_RUN_STATUS_TRANSITIONS,
  PAYROLL_RUN_STATUS_VALUES,
  PAYROLL_RUN_TYPE,
  PAYROLL_RUN_TYPE_VALUES,
  PAYROLL_RUN_CANCELLABLE_TYPES,
  PAYROLL_FUNDING_STATUS,
  PAYROLL_FUNDING_STATUS_VALUES,
  PAYROLL_FUNDING_STATUS_BADGE_TONES,
  PAYROLL_FUNDING_NOT_CHECKED_REASON,
  PAYROLL_FUNDING_NOT_CHECKED_REASON_VALUES,
  PAYROLL_ONBOARDING_STATUS,
  PAYROLL_ONBOARDING_STATUS_VALUES,
  PAYROLL_ONBOARDING_STATUS_BADGE_TONES,
  PAYROLL_ONBOARDING_STATUS_FRESH_FOR_MS,
  isOnboardingCompleted,
  SCHEDULE_ABSENCE_REASON,
  SCHEDULE_ABSENCE_REASON_VALUES,
  SELF_REPORTED_ABSENCE_REASONS,
};
